// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Scale,
  Building2,
  User,
  Phone,
  Mail,
  ExternalLink,
  AlertTriangle,
  CheckSquare,
  Clock,
  FileText,
  Copy,
  Archive,
  DollarSign,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { EditCaseInfoModal } from '@/components/cases/EditCaseInfoModal';
import { CaseTasks } from '@/components/tasks/CaseTasks';
import { CaseDeadlines } from '@/components/tasks/CaseDeadlines';
import { ArchiveCaseModal } from '@/components/cases/ArchiveCaseModal';
import { MobileEmptyState } from '../ui/MobileEmptyState';

type Tab = 'overview' | 'deadlines' | 'tasks' | 'movements';

const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  em_andamento: { label: 'Em andamento', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  active: { label: 'Em andamento', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  suspended: { label: 'Suspenso', tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  concluded: { label: 'Concluído', tone: 'bg-muted text-muted-foreground' },
  archived: { label: 'Arquivado', tone: 'bg-muted text-muted-foreground' },
};

const fmtDate = (d?: string) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const MobileCaseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [caseDetails, setCaseDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('*, clients:client_id(id, name, email, phone, mobile)')
        .eq('id', id)
        .single();
      if (error) throw error;
      setCaseDetails(data);
    } catch (err: any) {
      toast({
        title: 'Erro ao carregar processo',
        description: err.message,
        variant: 'destructive',
      });
      navigate('/pwa/cases');
    } finally {
      setLoading(false);
    }
  }, [id, toast, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const copyCnj = async () => {
    const num = caseDetails?.case_number_cnj || caseDetails?.case_number;
    if (!num) return;
    try {
      await navigator.clipboard.writeText(num);
      toast({ title: 'Número copiado' });
    } catch {}
  };

  const openInCNJ = () => {
    const num = (
      caseDetails?.case_number_cnj || caseDetails?.case_number || ''
    ).replace(/\D/g, '');
    if (!num) return;
    window.open(`https://www.cnj.jus.br/busca-processos/?numero=${num}`, '_blank');
  };

  if (loading || !caseDetails) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground">
        Carregando processo...
      </div>
    );
  }

  const st = STATUS_LABELS[(caseDetails.status || '').toLowerCase()];
  // Não usar case_type para inferir administrativo: case_type é área/tipo da causa.
  const isAdmin = !!caseDetails.is_administrative_process;
  const client = caseDetails.clients;
  const caseValue = Number(caseDetails.case_value) || 0;
  const movements = Array.isArray(caseDetails.datajud_json?.movimentos)
    ? caseDetails.datajud_json.movimentos
    : [];

  return (
    <div className="pb-6">
      {/* Header com voltar */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-2 px-2 py-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => navigate('/pwa/cases')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="text-sm font-semibold truncate flex-1">
            {isAdmin ? 'Processo Administrativo' : 'Processo Judicial'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => setEditOpen(true)}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>

        {/* Abas */}
        <div className="px-3 pb-2 flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: 'overview' as Tab, label: 'Resumo' },
            { id: 'deadlines' as Tab, label: 'Prazos' },
            { id: 'tasks' as Tab, label: 'Tarefas' },
            { id: 'movements' as Tab, label: 'Movimentações' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium border shrink-0 transition-colors',
                tab === t.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' && (
        <div className="p-3 space-y-3">
          {/* Card principal */}
          <div className="rounded-2xl bg-card border border-border p-4">
            <div className="flex items-start gap-3 mb-3">
              <div
                className={cn(
                  'h-12 w-12 rounded-xl flex items-center justify-center shrink-0',
                  isAdmin
                    ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
                    : 'bg-primary/15 text-primary',
                )}
              >
                {isAdmin ? (
                  <Building2 className="h-6 w-6" />
                ) : (
                  <Scale className="h-6 w-6" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                  {isAdmin ? 'Administrativo' : 'Judicial'}
                </p>
                <h1 className="font-bold text-lg leading-tight mt-0.5">
                  {caseDetails.title}
                </h1>
              </div>
            </div>

            {(caseDetails.case_number_cnj || caseDetails.case_number) && (
              <button
                onClick={copyCnj}
                className="w-full mt-2 rounded-lg bg-muted px-3 py-2 flex items-center justify-between active:bg-muted/70"
              >
                <span className="font-mono text-xs">
                  {caseDetails.case_number_cnj || caseDetails.case_number}
                </span>
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}

            <div className="flex flex-wrap gap-1.5 mt-3">
              {st && (
                <Badge className={`${st.tone} border-0 text-[10px]`}>
                  {st.label}
                </Badge>
              )}
              {caseDetails.priority && (
                <Badge className="bg-muted text-foreground border-0 text-[10px]">
                  {caseDetails.priority}
                </Badge>
              )}
              {caseDetails.area_of_law && (
                <Badge className="bg-primary/15 text-primary border-0 text-[10px]">
                  {caseDetails.area_of_law}
                </Badge>
              )}
              {caseDetails.archived && (
                <Badge className="bg-muted text-muted-foreground border-0 text-[10px]">
                  <Archive className="h-3 w-3 mr-1" />
                  Arquivado
                </Badge>
              )}
            </div>

            {(caseDetails.case_number_cnj || caseDetails.case_number) &&
              !isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full h-10"
                  onClick={openInCNJ}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-2" />
                  Consultar no CNJ
                </Button>
              )}
          </div>

          {/* Cliente */}
          {client && (
            <div className="rounded-2xl bg-card border border-border overflow-hidden">
              <div className="px-4 pt-3 pb-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Cliente
                </p>
              </div>
              <div className="px-4 py-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[15px] truncate">
                    {client.name}
                  </p>
                  {client.email && (
                    <p className="text-xs text-muted-foreground truncate">
                      {client.email}
                    </p>
                  )}
                </div>
              </div>
              <div className="border-t border-border grid grid-cols-3 divide-x divide-border">
                <a
                  href={client.mobile || client.phone ? `tel:${client.mobile || client.phone}` : '#'}
                  aria-disabled={!client.mobile && !client.phone}
                  className={cn(
                    'flex items-center justify-center gap-1.5 py-3 text-xs font-medium active:bg-muted',
                    (!client.mobile && !client.phone) &&
                      'opacity-40 pointer-events-none',
                  )}
                >
                  <Phone className="h-4 w-4 text-emerald-500" />
                  Ligar
                </a>
                <a
                  href={
                    client.mobile || client.phone
                      ? `https://wa.me/55${(client.mobile || client.phone).replace(/\D/g, '')}`
                      : '#'
                  }
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={!client.mobile && !client.phone}
                  className={cn(
                    'flex items-center justify-center gap-1.5 py-3 text-xs font-medium active:bg-muted',
                    (!client.mobile && !client.phone) &&
                      'opacity-40 pointer-events-none',
                  )}
                >
                  <Phone className="h-4 w-4 text-emerald-600" />
                  WhatsApp
                </a>
                <a
                  href={client.email ? `mailto:${client.email}` : '#'}
                  aria-disabled={!client.email}
                  className={cn(
                    'flex items-center justify-center gap-1.5 py-3 text-xs font-medium active:bg-muted',
                    !client.email && 'opacity-40 pointer-events-none',
                  )}
                >
                  <Mail className="h-4 w-4 text-sky-500" />
                  E-mail
                </a>
              </div>
            </div>
          )}

          {/* Info adicional */}
          <div className="rounded-2xl bg-card border border-border divide-y divide-border">
            {caseDetails.court && (
              <div className="px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tribunal</span>
                <span className="font-medium text-right ml-3 truncate">
                  {caseDetails.court}
                </span>
              </div>
            )}
            {caseDetails.vara && (
              <div className="px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Vara</span>
                <span className="font-medium text-right ml-3 truncate">
                  {caseDetails.vara}
                </span>
              </div>
            )}
            {caseDetails.jurisdiction && (
              <div className="px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Comarca</span>
                <span className="font-medium text-right ml-3 truncate">
                  {caseDetails.jurisdiction}
                </span>
              </div>
            )}
            {caseDetails.filing_date && (
              <div className="px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Distribuição</span>
                <span className="font-medium">{fmtDate(caseDetails.filing_date)}</span>
              </div>
            )}
            {caseDetails.lawyer_responsible && (
              <div className="px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Responsável</span>
                <span className="font-medium text-right ml-3 truncate">
                  {caseDetails.lawyer_responsible}
                </span>
              </div>
            )}
            {caseValue > 0 && (
              <div className="px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Valor da causa</span>
                <span className="font-bold text-primary">
                  {caseValue.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </span>
              </div>
            )}
          </div>

          {caseDetails.description && (
            <div className="rounded-2xl bg-card border border-border p-4">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                Descrição
              </p>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">
                {caseDetails.description}
              </p>
            </div>
          )}

          {/* Ações */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="h-12"
              onClick={() => navigate('/pwa/financial')}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Financeiro
            </Button>
            <Button
              variant="outline"
              className="h-12"
              onClick={() => setArchiveOpen(true)}
            >
              <Archive className="h-4 w-4 mr-2" />
              {caseDetails.archived ? 'Desarquivar' : 'Arquivar'}
            </Button>
          </div>
        </div>
      )}

      {tab === 'deadlines' && (
        <div className="p-3">
          <CaseDeadlines caseId={id!} />
        </div>
      )}

      {tab === 'tasks' && (
        <div className="p-3">
          <CaseTasks caseId={id!} />
        </div>
      )}

      {tab === 'movements' && (
        <div className="p-3">
          {movements.length === 0 ? (
            <MobileEmptyState
              icon={<FileText className="h-6 w-6" />}
              title="Sem movimentações"
              description={
                caseDetails.case_number_cnj
                  ? 'Sincronize com o CNJ/DataJud no desktop para ver as movimentações.'
                  : 'Vincule o nº CNJ para sincronizar.'
              }
            />
          ) : (
            <div className="rounded-2xl bg-card border border-border overflow-hidden divide-y divide-border">
              {movements.slice(0, 30).map((m: any, idx: number) => (
                <div key={idx} className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <p className="text-[11px] text-muted-foreground">
                      {fmtDate(m.dataHora || m.data)}
                    </p>
                  </div>
                  <p className="text-sm leading-snug">
                    {m.nome || m.descricao || 'Movimentação'}
                  </p>
                </div>
              ))}
              {movements.length > 30 && (
                <div className="px-4 py-3 text-center text-xs text-muted-foreground">
                  Mostrando 30 de {movements.length}. Veja tudo no desktop.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <EditCaseInfoModal
        caseDetails={caseDetails}
        isOpen={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => {
          setEditOpen(false);
          load();
        }}
      />

      <ArchiveCaseModal
        isOpen={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        caseId={caseDetails.id}
        caseTitle={caseDetails.title}
        onSuccess={() => {
          setArchiveOpen(false);
          load();
        }}
      />
    </div>
  );
};

export default MobileCaseDetail;
